import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../../config/authConfig';
import { useAuth } from '../../contexts/AuthContext';
import { authenticationService } from '../../services/authentication.service';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { toast } from 'react-toastify';
import './Login.scss';

const Login: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const { userRoles, isAuthenticated } = useAuth();
  const { instance, inProgress } = useMsal();
  const location = useLocation();

  // After the FISP-style setpassword flow completes, the done page sends users here with
  // ?passwordSet=1. The reset-password policy left an account + refresh-token in MSAL cache
  // that belongs to a different B2C user flow than the signin policy; if Login keeps it
  // around, `acquireTokenSilent` POSTs that token to the signin /token endpoint and gets a
  // 400 Bad Request (different audiences). Purge MSAL state and surface a confirmation toast
  // so the user signs in fresh with the new password.
  //
  // The /setpassword/done page already does a thorough sweep + hard reload, so by the time
  // we get here localStorage should be empty. This is belt-and-suspenders for the case where
  // a user lands on /login?passwordSet=1 without going through /done.
  // Survives the full page reloads a bounce causes, so the hand-off can be rate-limited across
  // them. Holds the timestamp of the last attempt, not a boolean: a loop re-enters within about
  // a second, whereas a person retrying takes far longer, so time is what separates the two.
  const HANDOFF_KEY = 'facilon.passwordSet.autoHandoffAt';
  const HANDOFF_MIN_GAP_MS = 20000;
  const handedOffRecently = (): boolean => {
    try {
      const last = Number(sessionStorage.getItem(HANDOFF_KEY) || 0);
      return last > 0 && Date.now() - last < HANDOFF_MIN_GAP_MS;
    } catch {
      return false; // no storage — behave as before, unthrottled
    }
  };
  const passwordSetShownRef = useRef(false);
  // Set once the purge below has finished, so the hand-off effect that follows knows the
  // MSAL cache is clean and can wait for MSAL to go idle before redirecting.
  const [passwordSetPurged, setPasswordSetPurged] = useState(false);
  useEffect(() => {
    if (passwordSetShownRef.current) return;
    if (new URLSearchParams(location.search).get('passwordSet') === '1') {
      passwordSetShownRef.current = true;
      (async () => {
        try {
          for (const acc of instance.getAllAccounts()) {
            try { await instance.clearCache({ account: acc }); } catch {}
          }
        } catch {}
        try { await instance.clearCache(); } catch {}
        try {
          const toRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (
              k.startsWith('msal.') ||
              k.includes('login.windows.net') ||
              k.includes('b2clogin.com') ||
              k.includes('-idtoken-') ||
              k.includes('-refreshtoken-') ||
              k.includes('-accesstoken-') ||
              k.includes('-account-') ||
              k.includes('interaction.status') ||
              k === 'facilon.setpassword.inProgress' ||
              // The app session as well (2026-09-08): a stale Facilon JWT kept
              // isAuthenticated true and re-entered the dashboard around this purge.
              k === 'JwtToken' ||
              k === 'tenantId' ||
              k === 'loginMethod' ||
              k === 'mustChangePassword' ||
              k.startsWith('currentUser.')
            ) {
              toRemove.push(k);
            }
          }
          toRemove.forEach((k) => localStorage.removeItem(k));
        } catch {}
        try {
          // MSAL v5 keeps its TEMPORARY cache (request state, interaction status) in
          // sessionStorage — which is per-tab. The tab that went through the set-password
          // loop accumulated broken temp state there, and sweeping only localStorage left
          // that tab bouncing forever while a fresh tab worked (observed 2026-09-11).
          // Deliberately keeps facilon.passwordSet.autoHandoffAt: that throttle is what
          // breaks redirect loops, and this purge runs on every lap of one.
          const toRemoveSession: string[] = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (!k) continue;
            if (
              k.startsWith('msal.') ||
              k.includes('login.windows.net') ||
              k.includes('b2clogin.com') ||
              k.includes('interaction.status') ||
              k === 'msal_redirect_in_progress'
            ) {
              toRemoveSession.push(k);
            }
          }
          toRemoveSession.forEach((k) => sessionStorage.removeItem(k));
        } catch {}
        // Only on a genuine arrival. Each bounce reloads the page and re-ran this effect, so the
        // confirmation was re-toasting endlessly (reported 2026-09-11).
        if (!handedOffRecently()) {
          toast.success('Your password has been set. Please sign in with your new password.');
        }
        // Hand-off to B2C happens in the effect below — NOT here. Calling loginRedirect at
        // this point threw `interaction_in_progress`: this effect runs on mount, while MSAL
        // is still in Startup/HandleRedirect processing its own redirect promise, and the
        // swallowed throw stranded the user on this local form (reported 2026-09-10).
        setPasswordSetPurged(true);
      })();
    }
  }, [location.search, instance]);

  // Hand off to the B2C hosted sign-in page once the purge is done AND MSAL is idle.
  // Gating on InteractionStatus.None is what makes the redirect actually fire: MSAL refuses
  // (and throws) while another interaction is in flight, and `inProgress` in the deps means
  // this re-runs the moment it settles instead of giving up like the swallowed catch did.
  const passwordSetRedirectRef = useRef(false);
  // Failsafe: never leave the user on a spinner. If the hand-off hasn't navigated away
  // within 10s (MSAL wedged, popup/redirect blocked), fall back to the local form.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('passwordSet') !== '1') {
      // A plain visit to /login means the previous flow is over — clear the throttle.
      try { sessionStorage.removeItem(HANDOFF_KEY); } catch { /* no storage */ }
      return;
    }
    const t = window.setTimeout(() => setLoading(false), 10000);
    return () => window.clearTimeout(t);
  }, [location.search]);
  useEffect(() => {
    if (!passwordSetPurged || passwordSetRedirectRef.current) return;
    if (inProgress !== InteractionStatus.None) return;
    // Loop-breaker (2026-09-11): B2C can answer the hand-off from the SSO session the password
    // reset just created, returning a token that still names the reset policy — which
    // LoginCallback's tfp guard bounces straight back here. Seen live as ~1.5 page loads a
    // second. Auto-redirect at most ONCE per tab; after that, show the form and let the
    // investor click Sign In, which cannot loop.
    if (handedOffRecently()) {
      // Drop ?passwordSet=1 from the address bar so a refresh is an ordinary visit to /login
      // instead of replaying the purge, the toast and this hand-off all over again.
      try { window.history.replaceState({}, '', '/login'); } catch { /* history unavailable */ }
      toast.info('Please sign in with the password you just set.');
      setLoading(false);
      return;
    }
    passwordSetRedirectRef.current = true;
    (async () => {
      try {
        try { sessionStorage.setItem(HANDOFF_KEY, String(Date.now())); } catch { /* no storage */ }
        // prompt:'login' stops B2C reusing the reset-policy session, so the token that comes
        // back is minted by the sign-in policy — the root cause of the bounce above.
        await instance.loginRedirect({ ...loginRequest, prompt: 'login' });
      } catch (e) {
        // Genuinely refused. Surface it instead of halting silently, and leave the local
        // Sign In control usable as the fallback path.
        console.error('[Login] B2C hand-off after set-password failed', e);
        toast.error('Could not open the sign-in page automatically. Please click Sign In.');
        passwordSetRedirectRef.current = false;
        setLoading(false);
      }
    })();
  }, [passwordSetPurged, inProgress, instance]);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Handle MSAL cached account on mount
  useEffect(() => {
    const accounts = instance.getAllAccounts();
    sessionStorage.removeItem('msal_redirect_in_progress');

    if (accounts.length > 0 && !localStorage.getItem('JwtToken')) {
      // MSAL will handle redirect via handleRedirectPromise
    }
  }, [instance]);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    // Stale-session guard (2026-09-08): a leftover Facilon JWT from an earlier session makes
    // isAuthenticated true and this effect would bounce to the dashboard BEFORE the
    // passwordSet purge below finishes — the last remaining path that skipped the fresh
    // B2C sign-in after set-password. The purge also removes the app keys now; this gate
    // kills the race synchronously.
    if (new URLSearchParams(location.search).get('passwordSet') === '1') return;
    if (isAuthenticated) {
      const roles = userRoles.length > 0 ? userRoles : authenticationService.getUserRoles();
      const isSuperAdmin = roles.some(role => role === 'PLATFORM_SUPER_ADMIN' || role.toUpperCase().includes('SUPER_ADMIN'));
      // DSR_ADMIN must be checked before the generic ADMIN match ("DSR_ADMIN".includes('ADMIN') is true)
      const isDsrAdmin = !isSuperAdmin && roles.some(role => role === 'DSR_ADMIN');
      const isAdmin = !isSuperAdmin && !isDsrAdmin && roles.some(role => role === 'ADMIN' || role.toUpperCase().includes('ADMIN'));
      const isServiceAgent = !isSuperAdmin && !isDsrAdmin && !isAdmin && roles.some(role => role === 'SERVICE_AGENT');

      if (isSuperAdmin) navigate('/admin/dashboard', { replace: true });
      else if (isDsrAdmin) navigate('/admin/dsr-admins', { replace: true });
      else if (isAdmin) navigate('/admin/dashboard', { replace: true });
      else if (isServiceAgent) navigate('/service-agent/dashboard', { replace: true });
      else navigate('/investor/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, userRoles, location.search]);

  // No app session: hand off to Azure B2C.
  useEffect(() => {
    if (isAuthenticated || inProgress !== InteractionStatus.None) return;

    // Skip auto-login when the user just completed the setpassword flow: the companion
    // useEffect above clears the reset-policy MSAL cache asynchronously, then hands off to
    // the B2C hosted page. Stay on the spinner meanwhile so this local form never flashes up
    // as a second, different-looking login screen; the hand-off's own failure path (and the
    // failsafe below) restore the form if the redirect can't happen.
    if (new URLSearchParams(location.search).get('passwordSet') === '1') {
      return;
    }

    let cancelled = false;

    const handleAuthentication = async () => {
      try {
        // prompt:'login' — Laravel parity (AzureService.php sent it on every hand-off). Without
        // it B2C answers from whatever SSO session sits on b2clogin.com, and until 2026-11-03
        // this effect also re-logged a cached MSAL account silently (acquireTokenSilent). On a
        // shared device both hand the NEXT person the PREVIOUS person's dashboard with no
        // credential screen — seen when an investor's spouse used the same browser. Whoever
        // types the password is who signs in; the 7-day Facilon JWT remains the convenience.
        await instance.loginRedirect({ ...loginRequest, prompt: 'login' });
      } catch (error) {
        const errorCode = (error as { errorCode?: string })?.errorCode;
        if (errorCode !== 'interaction_in_progress' && !cancelled) setLoading(false);
      }
    };

    handleAuthentication();
    return () => { cancelled = true; };
  }, [isAuthenticated, instance, inProgress]);

  /**
   * Retry the B2C hand-off. This screen is only reached when the automatic redirect could
   * not happen, so the button re-enters the same flow rather than offering a local sign-in:
   * authentication is Azure AD B2C only, and POST /api/login has been switched off.
   */
  const startSignIn = async () => {
    setLoading(true);
    try {
      await instance.loginRedirect({ ...loginRequest, prompt: 'login' });
    } catch (error) {
      const errorCode = (error as { errorCode?: string })?.errorCode;
      if (errorCode !== 'interaction_in_progress') {
        toast.error('Could not reach the sign-in service. Please try again.');
        setLoading(false);
      }
    }
  };

  if (loading) {
    return <LoadingSpinner message="Connecting to Facilon Services..." />;
  }

  return (
    <main className="facilon-login-page">
      <div className="login-background-overlay"></div>

      <div className="container login-container">
        <div className="login-wrapper">
          <div className="login-info-section">
            <div className="brand-logo-large">
              <img src="/assets/images/facilon-main-logo.png" alt="Facilon Services" />
            </div>
            <h1 className="text-neutral-800">The smarter way to <br/><span className="text-primary-500">manage your investments</span></h1>
            <p className="text-neutral-500">Access your professional console to track progress, submit documents, and engage with service providers.</p>
          </div>

          <div className="login-form-section">
            <div className="premium-login-card bg-surface rounded-xl border border-neutral-200">
              <div className="card-header-modern">
                <span className="welcome-label text-primary-500">Ready to get started?</span>
                <h2 className="text-neutral-900">Investor Login</h2>
              </div>

              <div className="modern-form">
                <p className="text-neutral-500 mb-4">
                  Sign in with your Facilon account. You will be taken to our secure
                  sign-in page to enter your email and password.
                </p>

                <button
                  className="btn w-100 mb-4 bg-primary-500 hover:bg-primary-600 text-white font-bold transition-all"
                  type="button"
                  onClick={startSignIn}
                  disabled={loading}
                >
                  {loading ? 'Connecting...' : 'Continue to sign in'}
                </button>

                <div className="login-footer-links text-neutral-500">
                  <span>New to Facilon?</span>
                  <Link to="/investor/register" className="register-link ms-2 text-primary-500 hover:text-primary-700 font-extrabold">Register Now</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
